import React, { useRef, useState, useEffect, useCallback } from "react";

import styles from "styles.module.css";
import Filter from "./Filter";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { OverLayLoading } from "ui/Loading";
import { useTranslation } from "react-i18next";
import { useInfoStore } from "store/error";
import { frozenSaleWeeklyGridColumn } from "./utils/GridColumns";
import { FrozenSalesWeeklyFilter, getFrozenSaleWeeklyApi } from "api/sales";
// @ts-ignore
import "./style.css";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import FrozenWeeklySalesMobileList from "./FrozenWeeklySalesMobileList";
import { FrozenSalesWeekly } from "interface/sales";
import { PageTypePath } from "utils/pages";

const pageSize = 60;

const FrozenSalesWeeklyPage = () => {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const [gridData, setGridData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1700);
	const [mobileData, setMobileData] = useState<FrozenSalesWeekly[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<FrozenSalesWeeklyFilter>({
		fromDate: new Date(),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const { setError } = useInfoStore();

	useEffect(() => {
		if (isMobileView) return;
		setIsLoading(true);
		getFrozenSaleWeeklyApi(filterOptions)
			.then((res) => {
				setGridData(res);
			})
			.catch((err) => {
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [filterOptions, isMobileView]);

	const requestMobileData = useCallback(
		(skip: number, take: number, resetData = false) => {
			if (mobileRequestInProgress.current || mobileDataFinished.current) {
				return;
			}

			mobileRequestInProgress.current = true;
			setMobileLoading(true);

			getFrozenSaleWeeklyApi(filterOptions)
				.then((incoming) => {
					const data = (incoming || []) as FrozenSalesWeekly[];

					if (data.length === 0) {
						mobileDataFinished.current = true;
						if (resetData) {
							setMobileData([]);
						}
						return;
					}

					mobileNextSkip.current = skip + take;

					if (resetData) {
						setMobileData(data);
					} else {
						setMobileData((prev) => [...prev, ...data]);
					}

					mobileDataFinished.current = true;
				})
				.finally(() => {
					mobileRequestInProgress.current = false;
					setMobileLoading(false);
				});
		},
		[filterOptions],
	);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);
		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) return;

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const exportGridAsPDF = () => {
		const fromDate = filterOptions.fromDate instanceof Date ? filterOptions.fromDate.toISOString() : "";
		const toDate = filterOptions.toDate instanceof Date ? filterOptions.toDate.toISOString() : "";
		localStorage.setItem("fromDate", fromDate);
		localStorage.setItem("toDate", toDate);

		window.open(`/${PageTypePath.Pdf}/0/2/`, "_blank");
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	return (
		<div className={styles.PageContainer}>
			<div ref={overlayRef}>
				<OverLayLoading show={isLoading} />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("FrozenSalesWeekly")}
				</legend>
			</div>
			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div ref={gridRef}>
				{isMobileView ? (
					<FrozenWeeklySalesMobileList
						items={mobileData}
						loading={mobileLoading}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
					/>
				) : (
					<GridWithGroupPaging
						gridColumns={frozenSaleWeeklyGridColumn()}
						gridData={gridData}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						pageable={false}
						excelProps={{
							fileName: `frozen_weekly_sales_${new Date().toISOString()}.xlsx`,
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default FrozenSalesWeeklyPage;
